/**
 * Wires the zoom-dialog's right-side thumbnail rail to swap a single active
 * image in the main pane (instead of the original behavior of scrolling
 * through a stacked list of all images).
 *
 * Markup contract — this script assumes the dialog contains:
 *   .dialog-thumbnails-list   — container of .dialog-thumbnails-list__thumbnail buttons
 *   .dialog-zoomed-gallery    — container of .product-media-container <li>s in the same order
 *   <drag-zoom-wrapper> inside each image <li>, exposing a public resetZoom() method
 *
 * The first <li> should be server-rendered with class `is-active`. CSS hides
 * non-active items via display:none so only one image is in layout at a time.
 */

/** @param {HTMLElement} dialog */
function init(dialog) {
  if (dialog.dataset.imageSwitcherInit === 'true') return;
  dialog.dataset.imageSwitcherInit = 'true';

  const thumbsList = dialog.querySelector('.dialog-thumbnails-list');
  const gallery = dialog.querySelector('.dialog-zoomed-gallery');
  if (!thumbsList || !gallery) return;

  const thumbButtons = /** @type {HTMLButtonElement[]} */ (
    Array.from(thumbsList.querySelectorAll('.dialog-thumbnails-list__thumbnail'))
  );
  const galleryItems = /** @type {HTMLElement[]} */ (
    Array.from(gallery.querySelectorAll('.product-media-container'))
  );
  if (thumbButtons.length === 0 || galleryItems.length === 0) return;

  // Listener uses event delegation so it works even if Horizon's native
  // handleThumbnailClick directive also runs (idempotent).
  thumbsList.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('.dialog-thumbnails-list__thumbnail');
    if (!(button instanceof HTMLButtonElement)) return;

    const index = thumbButtons.indexOf(button);
    if (index < 0 || !galleryItems[index]) return;

    activate(index);
  });

  /** @param {number} index */
  function activate(index) {
    // Reset zoom on whichever wrapper is currently active so the user doesn't
    // come back to a previously zoomed-in image with stale state.
    const previouslyActive = gallery.querySelector('.product-media-container.is-active');
    if (previouslyActive) {
      const wrapper = /** @type {any} */ (previouslyActive.querySelector('drag-zoom-wrapper'));
      if (wrapper && typeof wrapper.resetZoom === 'function') {
        wrapper.resetZoom();
      }
    }

    galleryItems.forEach((item, i) => item.classList.toggle('is-active', i === index));
    thumbButtons.forEach((btn, i) => {
      if (i === index) btn.setAttribute('aria-selected', 'true');
      else btn.removeAttribute('aria-selected');
    });
  }
}

function initAll() {
  document.querySelectorAll('zoom-dialog').forEach((dialog) => {
    if (dialog instanceof HTMLElement) init(dialog);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initAll);
} else {
  initAll();
}
